import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Layout, Menu, Button, App as AntdApp, Dropdown, Avatar, Space } from 'antd';
import AntdGlobal from './AntdGlobal';
import {
  DashboardOutlined,
  ClusterOutlined,
  TeamOutlined,
  SettingOutlined,
  CreditCardOutlined,
  AuditOutlined,
  FileSearchOutlined,
  GlobalOutlined,
  UserOutlined,
  DownOutlined,
} from '@ant-design/icons';
import './App.css';
import DashboardPage from './pages/Dashboard';
import NodeManagement from './pages/Infrastructure/NodeManagement';
import EmulatorManagement from './pages/Infrastructure/EmulatorManagement';
import DeviceProfileManagement from './pages/Infrastructure/DeviceProfileManagement';
import ImageManagement from './pages/Infrastructure/ImageManagement';
import TenantPage from './pages/Tenants';
import GlobalConfigurationPage from './pages/Configuration';
import AuditLogPage from './pages/AuditLog';
import DataManagementPage from './pages/DataManagement';
import TaskReportPage from './pages/DataManagement/TaskReport';
import AdvancedReportPage from './pages/AdvancedReport';
import ProxyManagementPage from './pages/NetworkProxy';
import AdminsPage from './pages/AccountManagement/Admins';
import UsersPage from './pages/AccountManagement/Users';
import ChangePasswordPage from './pages/Profile/ChangePassword';
import LoginPage from './pages/Auth/Login';
import RequireAuth from './auth/RequireAuth';
import { clearAccessToken } from './auth/storage';

const { Header, Content, Sider } = Layout;
const { SubMenu } = Menu;

// Placeholder components for pages
const BillingManagement = () => <div>Billing & Subscription Page</div>;

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === '/auth/login') {
    return (
      <Routes>
        <Route path="/auth/login" element={<LoginPage />} />
      </Routes>
    );
  }

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/') return ['/dashboard'];
    return [path];
  };

  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/account')) return ['account'];
    if (path.startsWith('/profile')) return ['profile'];
    if (path.startsWith('/infrastructure')) return ['infrastructure'];
    if (path.startsWith('/data-management')) return ['data-management'];
    return [];
  };

  const onLogout = () => {
    clearAccessToken();
    navigate('/auth/login', { replace: true });
  };

  const getUsername = () => {
    const token = localStorage.getItem('undroid.adminPortal.accessToken');
    if (!token) return 'Admin';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || 'Admin';
    } catch (e) {
      return 'Admin';
    }
  };

  const userMenuItems = [
    {
      key: 'password',
      icon: <SettingOutlined />,
      label: <Link to="/profile/password">Change Password</Link>,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: 'Logout',
      onClick: onLogout,
    },
  ];

  return (
    <RequireAuth>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider collapsible>
          <div className="logo" />
          <Menu theme="dark" selectedKeys={getSelectedKeys()} defaultOpenKeys={getOpenKeys()} mode="inline">
            <Menu.Item key="/dashboard" icon={<DashboardOutlined />}>
              <Link to="/dashboard">Dashboard</Link>
            </Menu.Item>
            
            <SubMenu key="infrastructure" icon={<ClusterOutlined />} title="Infrastructure">
              <Menu.Item key="/infrastructure/nodes">
                <Link to="/infrastructure/nodes">Node Management</Link>
              </Menu.Item>
              <Menu.Item key="/infrastructure/instances">
                <Link to="/infrastructure/instances">Emulator Instances</Link>
              </Menu.Item>
              <Menu.Item key="/infrastructure/device-profiles">
                <Link to="/infrastructure/device-profiles">Device Profiles</Link>
              </Menu.Item>
              <Menu.Item key="/infrastructure/images">
                <Link to="/infrastructure/images">Image Management</Link>
              </Menu.Item>
            </SubMenu>

            <Menu.Item key="/proxy" icon={<GlobalOutlined />}>
              <Link to="/proxy">Network Proxy</Link>
            </Menu.Item>
            <Menu.Item key="/tenants" icon={<TeamOutlined />}>
              <Link to="/tenants">Tenants</Link>
            </Menu.Item>
            <Menu.Item key="/configuration" icon={<SettingOutlined />}>
              <Link to="/configuration">Configuration</Link>
            </Menu.Item>
            <Menu.Item key="/billing" icon={<CreditCardOutlined />}>
              <Link to="/billing">Billing</Link>
            </Menu.Item>
            <Menu.Item key="/audit-log" icon={<AuditOutlined />}>
              <Link to="/audit-log">Audit Log</Link>
            </Menu.Item>
            <Menu.Item key="/data-management" icon={<FileSearchOutlined />}>
              <Link to="/data-management">Data Management</Link>
            </Menu.Item>

            <SubMenu key="account" icon={<TeamOutlined />} title="Account Management">
              <Menu.Item key="/account/admins">
                <Link to="/account/admins">Admins</Link>
              </Menu.Item>
              <Menu.Item key="/account/users">
                <Link to="/account/users">Users</Link>
              </Menu.Item>
            </SubMenu>
          </Menu>
        </Sider>
        <Layout className="site-layout">
          <Header className="site-layout-background" style={{ padding: '0 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
             <Dropdown menu={{ items: userMenuItems }}>
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar icon={<UserOutlined />} />
                  {getUsername()}
                  <DownOutlined />
                </Space>
             </Dropdown>
          </Header>
          <Content style={{ margin: '0 16px' }}>
            <div className="site-layout-background" style={{ padding: 24, minHeight: 360 }}>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                
                <Route path="/account/admins" element={<AdminsPage />} />
                <Route path="/account/users" element={<UsersPage />} />
                <Route path="/profile/password" element={<ChangePasswordPage />} />

                <Route path="/infrastructure" element={<Navigate to="/infrastructure/nodes" replace />} />
                <Route path="/infrastructure/nodes" element={<NodeManagement />} />
                <Route path="/infrastructure/instances" element={<EmulatorManagement />} />
                <Route path="/infrastructure/device-profiles" element={<DeviceProfileManagement />} />
                <Route path="/infrastructure/images" element={<ImageManagement />} />
                <Route path="/tenants" element={<TenantPage />} />
                <Route path="/configuration" element={<GlobalConfigurationPage />} />
                <Route path="/billing" element={<BillingManagement />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/data-management" element={<DataManagementPage />} />
                <Route path="/data-management/report/:taskId" element={<TaskReportPage />} />
                <Route path="/advanced-report/:taskId" element={<AdvancedReportPage />} />
                <Route path="/proxy" element={<ProxyManagementPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Content>
        </Layout>
      </Layout>
    </RequireAuth>
  );
};

function App() {
  return (
    <AntdApp>
      <AntdGlobal />
      <Router>
        <AppContent />
      </Router>
    </AntdApp>
  );
}

export default App;
